import { useEffect, useRef } from 'react';

import { Icon } from 'core/generic/elements/icons';

import { NavItemType } from '../../types';
import styles from '../nav/nav.module.scss';
import { SmartLink } from '../smart-link';
import subNavStyles from './nav-item.module.scss';

type NavItemProps = {
	item: Partial<NavItemType>;
	isActive: boolean;
	onMouseEnter: () => void;
	onMouseLeave: () => void;
	customStyles?: string;
	endItem?: boolean;
	startItem?: boolean;
};

export const NavItem = ({
	item,
	isActive,
	onMouseEnter,
	onMouseLeave,
	customStyles,
	endItem,
	startItem,
}: NavItemProps) => {
	const parentRef = useRef<HTMLAnchorElement>(null);
	const subNavElementRef = useRef<HTMLDivElement>(null);
	const subNavInnerElementRef = useRef<HTMLUListElement>(null);

	const hasChildren = item.items && item.items.length > 0;

	useEffect(() => {
		if (
			subNavElementRef.current &&
			parentRef.current &&
			subNavInnerElementRef.current &&
			isActive
		) {
			const parentWidth = parentRef.current.getBoundingClientRect().width;
			const subNavWidth =
				subNavElementRef.current.getBoundingClientRect().width;
			const subNavHeight =
				subNavElementRef.current.getBoundingClientRect().height;
			const windowHeight = window.innerHeight;
			if (subNavHeight < windowHeight + 100) {
				subNavElementRef.current.style.setProperty(
					'max-height',
					`${windowHeight - 100}px`
				);
			}

			if (!endItem && !startItem) {
				const leftPosition = (parentWidth - subNavWidth) / 2;
				subNavElementRef.current.style.setProperty(
					'left',
					`${leftPosition}px`
				);
			}
		}
	}, [subNavElementRef, parentRef, isActive, endItem]);

	return (
		<li
			className={`${styles['item']} ${customStyles}`}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
		>
			<SmartLink
				href={item.url}
				external={item.externalLink}
				onClick={(e) => {
					hasChildren && e.preventDefault();
				}}
				className={`body-medium bold ${styles['link']}`}
				target={item.target}
				aria-label={item.title}
				ref={parentRef}
			>
				<span>{item.title}</span>
				{hasChildren && (
					<Icon
						variant="ChevronDown"
						className={`${styles.chevron} `}
					/>
				)}
			</SmartLink>

			{hasChildren && (
				<div
					className={`subNav ${subNavStyles.subNav} ${
						endItem
							? subNavStyles.subNavEnd
							: startItem
								? subNavStyles.subNavStart
								: ''
					} ${isActive ? subNavStyles.open : ''} `}
					ref={subNavElementRef}
					role="menu"
				>
					<ul
						className={`${subNavStyles.subNavList} shadow-secondary-onlight-large`}
						ref={subNavInnerElementRef}
					>
						{item.items &&
							item.items
								?.flatMap((subItem) =>
									subItem.items && subItem.items.length > 0
										? subItem.items
										: [subItem]
								)
								.map((subItem) => (
									<li key={subItem.key}>
										<SmartLink
											href={subItem.url}
											external={subItem.externalLink}
											onClick={(e) => e.stopPropagation()}
											className={`body-medium bold ${styles['link']}`}
											target={subItem.target}
											aria-label={subItem.title}
											role="menuitem"
										>
											{subItem.title}
										</SmartLink>
									</li>
								))}
					</ul>
				</div>
			)}
		</li>
	);
};
